import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import UploadFileInput from '@repo/ui/file-input'
import { CheckMarkIcon, DownloadFile, XIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { PropsWithChildren, ReactNode } from 'react'

import useTranslate from '../../../lib/hooks/use-translate'
import {
  DocumentStatusEnum,
  documentStatusEnumMapping,
  DocumentStatusOption
} from '../../../lib/types/enums/common/document-status.enum'
import { FormControl, FormItem } from '../../elements/forms/form'
import InformationToolTip from '../../elements/information-tooltip'

type DocumentInformation = {
  tooltipInformation?: ReactNode
  subInformation?: string
}

type Props = {
  title: string
  label?: string
  files?: IFile[]
  filePlaceholder?: string
  status?: DocumentStatusEnum
  type?: DocumentType
  mapping?: Record<string, DocumentStatusOption>
  statusPosition?: 'inside' | 'outside'
  requireVerification?: boolean
  informationTooltip?: DocumentInformation
  disabled?: boolean
  className?: string
  errorMessage?: string
  canRemove?: boolean
  customBadge?: ReactNode
  onStatusChange?: (status: DocumentStatusEnum) => void
  onFilesChange: (files: IFile[]) => void
  onRemoveCard?: () => void
  id?: string
}

const DocumentCard = ({
  id,
  children,
  label,
  filePlaceholder,
  title,
  status,
  statusPosition,
  requireVerification,
  disabled,
  files,
  className,
  errorMessage,
  informationTooltip,
  mapping = documentStatusEnumMapping,
  canRemove = false,
  customBadge,
  onStatusChange,
  onFilesChange,
  onRemoveCard
}: PropsWithChildren<Props>) => {
  const { t } = useTranslate()

  const isOutside = statusPosition === 'outside' && status
  const isInside = statusPosition === 'inside' && status

  return (
    <div className={cn('w-full relative', className)} id={id}>
      {isOutside && mapping && (
        <div className="flex gap-4 mb-2 items-center">
          <Typography
            variant="text"
            color={disabled ? tailwindColors.grey.light.active : tailwindColors.grey.DEFAULT}
          >
            {title}
          </Typography>
          {customBadge ? (
            customBadge
          ) : (
            <Badge
              variant={mapping[status].variant}
              icon={mapping[status].icon}
              text={t(mapping[status].label)}
              iconPosition="start"
            />
          )}
        </div>
      )}
      {canRemove && !disabled && (
        <Button
          variant="ghost"
          icon={XIcon}
          type="button"
          size="xsmall"
          className="absolute -top-2 -right-1 mobile:right-1 mobile:-top-3 rounded-2xl flex items-center justify-center cursor-pointer "
          onClick={onRemoveCard}
        />
      )}

      <div
        className={cn('p-4 rounded-xl w-full border border-grey-light shadow  ', {
          'bg-white-active': disabled,
          'border border-red': errorMessage
        })}
      >
        {requireVerification && !disabled && (
          <div
            className={cn('flex justify-end pb-3 items-center gap-1', {
              'pointer-events-none': files?.length === 0
            })}
          >
            <div
              onClick={() => {
                //if its already refused, clicking again should set it back to received
                if (status === DocumentStatusEnum.REFUSED) {
                  onStatusChange?.(DocumentStatusEnum.RECEIVED)
                  return
                }
                onStatusChange?.(DocumentStatusEnum.REFUSED)
              }}
              className={cn('bg-grey-light rounded-sm p-2 cursor-pointer ', {
                'bg-red': status === DocumentStatusEnum.REFUSED
              })}
            >
              <XIcon
                height="13"
                width="13"
                color={
                  status === DocumentStatusEnum.REFUSED
                    ? tailwindColors.white.DEFAULT
                    : tailwindColors.grey.lighter
                }
              />
            </div>
            <div
              onClick={() => {
                if (status === DocumentStatusEnum.VERIFIED) {
                  onStatusChange?.(DocumentStatusEnum.RECEIVED)
                  return
                }
                onStatusChange?.(DocumentStatusEnum.VERIFIED)
              }}
              className={cn('bg-grey-light rounded-sm p-2 cursor-pointer', {
                'bg-green': status === DocumentStatusEnum.VERIFIED
              })}
            >
              <CheckMarkIcon
                height="13"
                width="13"
                color={
                  status === DocumentStatusEnum.VERIFIED
                    ? tailwindColors.white.DEFAULT
                    : tailwindColors.grey.lighter
                }
              />
            </div>
          </div>
        )}
        {isInside ? (
          <div className="flex items-center justify-between w-full gap-2">
            <div className="flex items-center gap-2">
              <div>
                <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
                  {title}
                </Typography>
                {informationTooltip?.subInformation && (
                  <Typography
                    variant="label-medium"
                    color={tailwindColors.blue.DEFAULT}
                    style={{ lineHeight: '16px' }}
                  >
                    {informationTooltip.subInformation}
                  </Typography>
                )}
              </div>
              {informationTooltip?.tooltipInformation && (
                <InformationToolTip>{informationTooltip.tooltipInformation}</InformationToolTip>
              )}
            </div>

            <Badge
              variant={documentStatusEnumMapping[status].variant}
              icon={documentStatusEnumMapping[status].icon}
              text={t(documentStatusEnumMapping[status].label)}
              iconPosition="start"
            />
          </div>
        ) : (
          <div />
        )}
        <div className="h-2" />
        {children}

        <FormItem className={'flex-1'}>
          <FormControl>
            <UploadFileInput
              label={label ?? t('file.upload.title')}
              labelType="label-medium"
              disabled={disabled}
              placeholder={filePlaceholder ?? t('file.upload.drag.and.drop')}
              types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF]}
              icon={DownloadFile}
              files={files}
              onFilesChange={onFilesChange}
              previewOnClick
              showDeleteIcon={!disabled}
            />
          </FormControl>
        </FormItem>
      </div>
      {errorMessage && (
        <Typography variant="small" color={tailwindColors.red.DEFAULT}>
          {errorMessage}
        </Typography>
      )}
    </div>
  )
}

export default DocumentCard
